import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, ExternalLink, BadgeCheck } from 'lucide-react';
import { FaEtsy } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { REVIEWS, ETSY_REVIEWS_URL } from '../config/reviews';
import { getTrustStats } from '../config/trustStats';
import useEtsyLive from '../hooks/useEtsyLive';

const useLiveReviews = () => {
  const live = useEtsyLive();
  return live && live.reviews.length >= 3 ? live : null;
};

const T = {
  en: {
    eyebrow: 'Etsy customer reviews',
    title: 'Developed with real-world feedback',
    subtitle: 'A few reviews reproduced from our Etsy shop.',
    subtitleLive: 'Recent reviews, pulled directly from our Etsy shop.',
    buyer: 'Verified Etsy buyer',
    viewAll: 'Read all reviews on Etsy',
    prev: 'Previous reviews',
    next: 'Next reviews',
    disclaimer: 'Customer reviews reflect personal opinions and experiences. They are not medical advice and do not modify the intended function or limitations of MyGuardPod cases.',
    stars: (n) => `${n} out of 5 stars`,
  },
  fr: {
    eyebrow: 'Avis clients Etsy',
    title: 'D\u00e9velopp\u00e9 \u00e0 partir de retours d\u2019utilisateurs r\u00e9els',
    subtitle: 'Quelques avis reproduits depuis notre boutique Etsy.',
    subtitleLive: 'Avis r\u00e9cents, r\u00e9cup\u00e9r\u00e9s directement depuis notre boutique Etsy.',
    buyer: 'Acheteur Etsy v\u00e9rifi\u00e9',
    viewAll: 'Lire tous les avis sur Etsy',
    prev: 'Avis pr\u00e9c\u00e9dents',
    next: 'Avis suivants',
    disclaimer: 'Les avis clients refl\u00e8tent des opinions et exp\u00e9riences personnelles. Ils ne constituent pas un avis m\u00e9dical et ne modifient ni la fonction pr\u00e9vue ni les limites des \u00e9tuis MyGuardPod.',
    stars: (n) => `${n} \u00e9toiles sur 5`,
  },
};

const formatDate = (iso, language) => {
  if (!iso) return null;
  const [y, m] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', {
    month: 'long',
    year: 'numeric',
  });
};

const ReviewCard = ({ review, c, language }) => {
  const lang = language === 'fr' ? 'fr' : 'en';
  const text = typeof review.text === 'string' ? review.text : review.text[lang];
  const author = review.live ? c.buyer : review.author[lang];
  return (
  <article
    data-testid={`etsy-review-${review.id}`}
    className="snap-start shrink-0 w-[85%] sm:w-[48%] lg:w-[32%] bg-white rounded-2xl border border-neutral-200 p-6 sm:p-7 flex flex-col"
  >
    <div className="flex items-center justify-between mb-4">
      <div className="flex gap-0.5" role="img" aria-label={c.stars(review.rating)}>
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${i < review.rating ? 'text-[#E31B23] fill-[#E31B23]' : 'text-neutral-300'}`}
          />
        ))}
      </div>
      {review.live && <BadgeCheck className="w-4 h-4 text-neutral-400" aria-hidden="true" />}
    </div>
    <blockquote className="font-manrope text-neutral-700 leading-relaxed flex-1" lang={review.language || undefined}>
      {'\u201c'}{text}{'\u201d'}
    </blockquote>
    <footer className="mt-5 pt-4 border-t border-neutral-100">
      <p className="font-outfit font-bold text-sm text-[#1a1a1a]">{author}</p>
      {(review.country || review.date) && (
        <p className="font-manrope text-xs text-neutral-500 mt-0.5">
          {[review.country, formatDate(review.date, language)].filter(Boolean).join(' \u00b7 ')}
        </p>
      )}
    </footer>
  </article>
  );
};

const EtsyReviews = () => {
  const { language } = useLanguage();
  const c = T[language === 'fr' ? 'fr' : 'en'];
  const live = useLiveReviews();
  const stats = getTrustStats(language, live);
  const trackRef = useRef(null);
  const reviews = live ? live.reviews.map((r) => ({ ...r, live: true })) : REVIEWS;
  const ratingValue = stats.rating.value;
  const reviewCount = stats.reviews.value;

  const scroll = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  return (
    <section data-testid="etsy-reviews-section" data-source={live ? 'etsy' : 'curated'} className="py-24 sm:py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-neutral-500 mb-3">{c.eyebrow}</p>
            <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1a1a1a] mb-3">
              {c.title}
            </h2>
            <p className="font-manrope text-neutral-600">{live ? c.subtitleLive : c.subtitle}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2" data-testid="etsy-reviews-rating">
              <Star className="w-5 h-5 text-[#E31B23] fill-[#E31B23]" />
              <span className="font-outfit font-black text-2xl text-[#1a1a1a]">
                {ratingValue}
                <span className="text-base text-neutral-500">{stats.rating.suffix}</span>
              </span>
              <span className="font-manrope text-sm text-neutral-500">
                {'\u00b7'} {reviewCount} {stats.reviews.label}
              </span>
            </div>
            <div className="hidden sm:flex gap-2">
              <button
                type="button"
                onClick={() => scroll(-1)}
                aria-label={c.prev}
                data-testid="etsy-reviews-prev"
                className="p-2.5 rounded-full border border-neutral-300 text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white hover:border-[#1a1a1a] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => scroll(1)}
                aria-label={c.next}
                data-testid="etsy-reviews-next"
                className="p-2.5 rounded-full border border-neutral-300 text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white hover:border-[#1a1a1a] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>

        <div
          ref={trackRef}
          data-testid="etsy-reviews-track"
          className="flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} c={c} language={language} />
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <a
            href={ETSY_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="etsy-reviews-view-all"
            className="inline-flex items-center gap-2 self-start bg-[#1a1a1a] hover:bg-[#E31B23] text-white font-outfit font-bold px-6 py-3 rounded-full transition-colors"
          >
            <FaEtsy className="w-4 h-4" />
            {c.viewAll}
            <ExternalLink className="w-4 h-4" />
          </a>
          <p className="font-manrope text-xs text-neutral-500 leading-relaxed sm:max-w-md sm:text-right">
            {c.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
};

export default EtsyReviews;
